import { Link } from 'gatsby'
import { useLocation } from '@reach/router'
import { useState } from 'react'
import { useAccountContext } from 'src/components/account/context'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { useMobile } from 'src/hooks/useMobile'
import { useCartToggleButton } from 'src/sdk/cart/useCartToggleButton'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import {
  checkPageType,
  getStandardObject,
} from 'src/utils/amplitude/getStandardObject'
import { checkEnviromentIsB2B } from 'src/utils/checkEnviroment'
import { redirectToAccountOrLogin } from 'src/utils/redirectToAccountUrl'
import { BagCartOutline } from 'src/components/Icons/BagCartOutline'
import { NewIconHeartOutline } from 'src/components/Icons/NewIconHeartOutline'
import Logo from 'src/components/ui/Logo'
import { Stripe } from 'src/components/global/Stripe'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import AbandonedCart from '../../cart/AbandonedCart/index'
import CustomBox from '../CustomBox'
import HeaderMobile from './HeaderMobile/HeaderMobile'
import LoginDropdown from './LoginDropdown/LoginDropdown'
import { LoginButton } from './LoginButton/LoginButton'
import Menu from './Menu'
import { PostalCodeInput } from './PostalCode'
import { SearchInput } from '../SearchInput'
import { ButtonIcon } from './ButtonIcon'
import ButtonClube from './ButtonClube/ButtonClube'
import { TurboPickupButton } from './TurboPickupInPoint'
import LogoProDesk from '../../../images/decathlonProLogoDesk.svg'
import LogoProMobile from '../../../images/decathlonProLogoMobile.svg'

import './header.scss'

function Header() {
  const { pageData } = useGlobalContext()
  const [isOpen, setIsOpen] = useState(false)
  const { isMobile } = useMobile()
  const { itemsTotal } = useCheckout()
  const btnPropsCart = useCartToggleButton()
  const { isLoggedIn } = useAccountContext()

  const standardObject = getStandardObject(pageData)
  const { pathname } = useLocation()
  const pageType = checkPageType(pathname)

  const cartButtonAction = btnPropsCart

  const isB2B = checkEnviromentIsB2B()

  const [showLoginDropdown, setShowLoginDropdown] = useState(false)
  const { openModal, closeModal } = useModalSheet()

  const onClickLogin = () => {
    const redirectUrl = redirectToAccountOrLogin(isB2B, isLoggedIn, pathname)

    if (!isLoggedIn) {
      dispatchAmplitudeEvent({
        eventName: 'Account Process Entered',
        eventData: {
          ...standardObject,
        },
      })
    }

    dispatchAmplitudeEvent({
      eventName: 'Click Icons',
      eventData: {
        ...standardObject,
        'click name': 'login',
      },
    })

    if (!isMobile) {
      window.location.href = redirectUrl

      return undefined
    }

    return openModal({
      content: <LoginDropdown onClose={closeModal} />,
      header: { isVisible: false, component: undefined },
    })
  }

  const handleLogoClick = () => {
    dispatchAmplitudeEvent({
      eventName: 'Click Icons',
      eventData: {
        ...standardObject,
        'click name': 'logo_home',
      },
    })
  }

  return (
    <>
      <Stripe />
      <AbandonedCart />

      <HeaderMobile
        onClickLogin={onClickLogin}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        handleLogoClick={handleLogoClick}
      />

      <header className="hidden restructure-small-desktop:block">
        <div
          data-component="header-container"
          className="border-b header-container sticky w-screen z-[9998] md:flex md:pb-0 md:w-full border-restructure-border-secondary bg-restructure-background-primary py-4 restructure-small-desktop:pt-lg restructure-small-desktop:pb-0"
        >
          <div
            className="w-full mx-auto px-md flex flex-col justify-between relative"
            style={{
              maxWidth: '1680px',
            }}
          >
            <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-3">
              {isB2B && (
                <Link
                  to="/"
                  className="w-[200px] restructure-small-desktop:w-[250px] flex items-center h-[24px] restructure-small-desktop:static restructure-small-desktop:top-0 restructure-small-desktop:left-0 restructure-small-desktop:translate-x-[0] absolute top-[7px] left-[50%] translate-x-[-50%]"
                  aria-label="Go to Decathlon home"
                  title="Go to Decathlon home"
                >
                  <img
                    className="w-full object-contain"
                    src={isMobile ? LogoProMobile : LogoProDesk}
                    alt="decathlon pro logo"
                  />
                </Link>
              )}
              {!isB2B && (
                <Link
                  to="https://www.decathlon.com.br"
                  className="flex items-center h-[1.625rem]"
                  aria-label="Go to Decathlon home"
                  title="Go to Decathlon home"
                  onClick={handleLogoClick}
                >
                  <Logo width={186} height={28} bgFill="#3643BA" />
                  <CustomBox
                    as={pageType === 'home' ? 'h1' : 'span'}
                    className="sr-only"
                  >
                    Decathlon
                  </CustomBox>
                </Link>
              )}

              <div className="hidden restructure-small-desktop:block">
                <PostalCodeInput variant="desktop" />
              </div>

              <div className="order-4 mt-4 restructure-small-desktop:mt-0 restructure-small-desktop:order-1 w-full restructure-small-desktop:max-w-[523px]">
                <SearchInput isOpen={isOpen} setIsOpen={setIsOpen} />
              </div>

              <div className="hidden restructure-medium-desktop:block restructure-small-desktop:order-2 mb-7 restructure-small-desktop:mb-0 rounded-[88px] bg-[#E1E3F5] min-w-[225px]">
                <ButtonClube />
              </div>

              <span className="restructure-small-desktop:order-5 hidden restructure-small-desktop:flex">
                <TurboPickupButton />
              </span>

              <ul className="flex items-center order-2 gap-5 sm:gap-0 restructure-small-desktop:order-6">
                {!isB2B && (
                  <li>
                    <ButtonIcon
                      padding="p-0"
                      link="/account/wishlist"
                      onClick={() => {
                        dispatchAmplitudeEvent({
                          eventName: 'Click Icons',
                          eventData: {
                            ...standardObject,
                            'click name': 'wishlist',
                          },
                        })
                        window.location.href = '/account/wishlist'
                      }}
                    >
                      <NewIconHeartOutline
                        aria-label="Botao para redirecionar para a wishlist"
                        data-testid="wish-list-button-icon"
                        className="w-6 h-6 restructure-small-desktop:w-lg restructure-small-desktop:h-lg"
                      />
                    </ButtonIcon>
                  </li>
                )}

                <li
                  onMouseEnter={() => {
                    !isMobile && setShowLoginDropdown(true)
                  }}
                  onMouseLeave={() => setShowLoginDropdown(false)}
                >
                  <LoginButton
                    showLoginDropdown={showLoginDropdown && !isB2B}
                    onClick={onClickLogin}
                  />
                </li>

                <li className="hidden restructure-small-desktop:flex">
                  <ButtonIcon
                    padding="p-0"
                    badgeValue={itemsTotal}
                    {...cartButtonAction}
                  >
                    <BagCartOutline
                      aria-label="botão abrir mini cart"
                      className="w-6 h-6 restructure-small-desktop:w-lg restructure-small-desktop:h-lg"
                    />
                  </ButtonIcon>
                </li>
              </ul>
            </div>
            <Menu />
          </div>
        </div>
      </header>
    </>
  )
}

export default Header
